import type { AccountKey, MailboxInfo } from "owa-client-types";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { isMChatFeatureEnabled } from "owa-copilot-settings-utils";
import { BizChatSidepaneEnablementCodes } from "owa-copilot-settings-store/lib/utils/enablementCodes/BizChatSidepaneEnablementCodes";
import type { CopilotEnablementDiagnostics } from "owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes";
import { getBizchatAcquisitionSettings } from "owa-sidepane-bizchat-store";
import { updateBizchatEnabledMutator } from "owa-sidepane-bizchat-store/lib/mutators/updateBizchatEnabledMutator";
import { UserCohort } from "owa-bizchat-types/lib/UserCohort";
import { lazyGetM365Acquisition } from "owa-m365-acquisitions/lib/lazyFunction";
import getBizChatAppId from "owa-m365-acquisitions/lib/utils/getBizChatAppId";
import { logCoreUsage } from "owa-analytics";
import { isVoltronEnabled } from "owa-capabilities-definitions/lib/isVoltronEnabled";
import { isBizchatSidepaneDisabledByPolicy } from "./isBizchatSidepaneDisabledByPolicy";
import { logBizchatSidepaneEnablementDiagnostics } from "./logBizchatSidepaneEnablementDiagnostics";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { isVanityDomain } from "owa-tokenprovider";
import { getHostHub } from "owa-config/lib/getHostHub";
import isConsumer from "owa-session-store/lib/utils/isConsumer";
import { getCurrentModule } from "owa-app-module-store";
import { isServiceRequestSupportedForMailbox } from "owa-service/lib/utils/isServiceRequestSupportedForMailbox";
import isDeepLink from "owa-url/lib/isDeepLink";
import { getUnifiedInboxResolvedMailboxInfoOrSelf } from "owa-session-store";

const isCheckingForBizchatEnabled = new Set<AccountKey>();

export async function refreshBizchatSidepaneAcquisition(mailboxInfo: MailboxInfo): Promise<void> {
    const effectiveMailboxInfo = getUnifiedInboxResolvedMailboxInfoOrSelf(mailboxInfo);
    const accountKey = getAccountKeyForMailboxInfo(effectiveMailboxInfo);
    const acquisition = await lazyGetM365Acquisition.importAndExecute(getBizChatAppId(effectiveMailboxInfo), "network-only", effectiveMailboxInfo, true /* shouldIgnoreUserPreferences */);
    
    updateBizchatEnabledMutator(accountKey, acquisition.acquisition !== undefined, acquisition.acquisition?.titleDefinition?.eligibility ?? undefined);
}

/**
 * Checks if Bizchat is enabled for the given mailbox.
 * @param mailboxInfo MailboxInfo for which to check Bizchat enablement
 * @param shouldIgnoreUserPreferences Whether to ignore the user's Copilot preference
 * @param isSupportedByVoltron Whether the calling feature supports the Voltron sidepane
 * @returns Returns the map.get(key) which can be undefined (network call hasn't finished),
 *          or true/false if it has finished.
 */
export function isBizchatSidepaneEnabled(mailboxInfo?: MailboxInfo, shouldIgnoreUserPreferences?: boolean, isSupportedByVoltron = false): boolean | undefined {
    const effectiveMailboxInfo = getUnifiedInboxResolvedMailboxInfoOrSelf(mailboxInfo ?? getModuleContextMailboxInfo());
    
    if (isVoltronEnabled(effectiveMailboxInfo) && !isSupportedByVoltron) {
        return false;
    }
    
    // For UIB, check the real mailboxInfo that will be used for bizchat
    // Return false for PST File and Msg File as Bizchat is not supported
    if (!isServiceRequestSupportedForMailbox(effectiveMailboxInfo)) {
        return false;
    }
    
    // Disable BizChat sidepane for deeplink windows
    if (isDeepLink()) {
        return false;
    }
    
    // This check is for the user turning OFF Copilot via Settings, and the license check from OCS.
    // It should have precedence over the acquisition check. It also uses a cached value from localStorage.
    const diagnostics: CopilotEnablementDiagnostics = { code: undefined };
    const isMChatEnabledForMailbox = isMChatFeatureEnabled(effectiveMailboxInfo, true /* skipLanguageCheck */, shouldIgnoreUserPreferences, diagnostics);
    if (!isMChatEnabledForMailbox) {
        logBizchatSidepaneEnablementDiagnostics(effectiveMailboxInfo, diagnostics);
        return isMChatEnabledForMailbox;
    }
    
    // Check for OCPS policies that might disable Bizchat sidepane and early return if it's disabled.
    if (isBizchatSidepaneDisabledByPolicy(effectiveMailboxInfo, diagnostics)) {
        logBizchatSidepaneEnablementDiagnostics(effectiveMailboxInfo, diagnostics);
        return false;
    }
    
    if (!isGlobalFeatureEnabled("mon-copilot-bizchat-vanity-killswitch") && isVanityDomain()) {
        diagnostics.code = BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_DISABLED_VANITY_DOMAIN;
        logBizchatSidepaneEnablementDiagnostics(effectiveMailboxInfo, diagnostics);
        return false;
    }
    
    // Check if the mailbox is enabled for Bizchat in the Bizchat shared store
    const accountKey = getAccountKeyForMailboxInfo(effectiveMailboxInfo);
    const isBizchatSidepaneEnabledForMailbox = getBizchatAcquisitionSettings(accountKey);
    if (isBizchatSidepaneEnabledForMailbox === undefined) {
        // If the value is undefined, it means we need to check if Bizchat is enabled for this mailbox
        
        if (isCheckingForBizchatEnabled.has(accountKey)) {
            // If we are already checking for this mailbox, return undefined to avoid multiple accesses
            // to the Apollo cache since this can cause overhead and multiple components rely on this call
            return undefined;
        }
        isCheckingForBizchatEnabled.add(accountKey);
        // If the value is not set, we need to fetch it from M365 Acquisitions
        lazyGetM365Acquisition
            .importAndExecute(getBizChatAppId(effectiveMailboxInfo), "cache-first", effectiveMailboxInfo, true /* shouldIgnoreUserPreferences */)
            .then(acquisition => {
            const hasAcquisition = acquisition.acquisition !== undefined;
            const eligibility = acquisition.acquisition?.titleDefinition?.eligibility;
            const isCopilotEligibleServiceValue = eligibility?.isCopilotEligible;
            
            logCoreUsage("OutlookCopilotChat_HasBizChatAcquisition", effectiveMailboxInfo, {
                hasAcquisition,
                isCopilotEligibleServiceValue: String(isCopilotEligibleServiceValue),
                isCopilotEligibleDefaultedToTrue: isCopilotEligibleServiceValue === null ||
                    isCopilotEligibleServiceValue === undefined,
                isCopilotEligibleEffectiveValue: isCopilotEligibleServiceValue ?? true,
                userCohort: (eligibility?.cohort?.toLowerCase() as UserCohort) ?? UserCohort.None,
                fromCache: !acquisition.recentlyInstalled,
            });
            
            // Update the store with the acquisition result and reset the checking flag
            updateBizchatEnabledMutator(accountKey, hasAcquisition, eligibility ?? undefined);
            isCheckingForBizchatEnabled.delete(accountKey);
        });
        if (!isGlobalFeatureEnabled("mon-copilot-bizchat-eligibility-killswitch")) {
            // Return undefined while the acquisition check is in progress so we don't log multiple times
            return undefined;
        }
    }
    
    const hasM365Acquisition = isBizchatSidepaneEnabledForMailbox?.hasAcquisition;
    const isCopilotEligible = isBizchatSidepaneEnabledForMailbox?.isCopilotEligible;
    const cohort = isBizchatSidepaneEnabledForMailbox?.userCohort;
    if (hasM365Acquisition !== undefined) {
        // If we have a definitive answer for hasM365Acquisition, log the result
        if (hasM365Acquisition) {
            if (isCopilotEligible === false) {
                diagnostics.code =
                    BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_COPILOT_INELIGIBLE;
            }
            else {
                diagnostics.code = BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_ENABLED;
            }
        }
        else {
            diagnostics.code =
                BizChatSidepaneEnablementCodes.BIZCHAT_SIDEPANE_ACQUISITION_NOT_FOUND;
        }
        logBizchatSidepaneEnablementDiagnostics(effectiveMailboxInfo, diagnostics, cohort);
    }
    
    // Early return for Starter users in Teams Calendar
    if (!isGlobalFeatureEnabled("cal-teams-copilot-starter-license-check-ripcord") &&
        getHostHub() === "teamshub" &&
        getCurrentModule() === "Calendar" &&
        !isConsumer() &&
        cohort === UserCohort.BCWAF) {
        return false;
    }
    
    if (isGlobalFeatureEnabled("mon-copilot-bizchat-eligibility-killswitch")) {
        return hasM365Acquisition;
    }
    else {
        return hasM365Acquisition && isCopilotEligible !== false;
    }
}
